import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Raw client for the refresh call itself — bypasses the global interceptor
// so a failing refresh can never recurse.
const rawClient = axios.create();

export const SESSION_EXPIRED_EVENT = 'auth:session-expired';
export const TOKEN_REFRESHED_EVENT = 'auth:token-refreshed';

// Auth endpoints manage their own 401 handling (login form errors,
// checkAuth retry logic, the refresh call itself).
const SKIP_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout', '/auth/me', '/auth/sso'];

let refreshPromise = null;

function clearStoredAuth() {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_refresh_token');
    localStorage.removeItem('crm_impersonating');
}

/**
 * Exchange the stored refresh token for a new access token.
 * Single-flight: concurrent callers share one in-flight refresh.
 * Returns the new access token, or null if the session is truly over
 * (in which case stored auth is cleared and SESSION_EXPIRED_EVENT fires).
 */
export function refreshAccessToken() {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const refreshToken = localStorage.getItem('crm_refresh_token');
        if (!refreshToken) {
            expireSession();
            return null;
        }
        try {
            const res = await rawClient.post(`${API}/auth/refresh`, { refresh_token: refreshToken }, { timeout: 15000 });
            const { access_token: newToken, refresh_token: newRefresh } = res.data;
            localStorage.setItem('crm_token', newToken);
            if (newRefresh) localStorage.setItem('crm_refresh_token', newRefresh);
            window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: { token: newToken } }));
            return newToken;
        } catch (err) {
            // Only end the session on a definitive rejection. Network blips or
            // 5xx during a deploy keep the tokens so a later attempt can succeed.
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                expireSession();
                return null;
            }
            throw err;
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

function expireSession() {
    const wasLoggedIn = !!localStorage.getItem('crm_token');
    clearStoredAuth();
    if (wasLoggedIn) {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
}

/**
 * Decode the exp claim (seconds since epoch) from a JWT without verifying it.
 * Returns null for opaque session tokens (Google OAuth) or malformed input.
 */
export function getTokenExpiry(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
        return null;
    }
}

let interceptorInstalled = false;

/**
 * Global axios response interceptor: on a 401 from an authenticated request,
 * silently refresh the access token and retry the original request once.
 * If the refresh fails the session-expired event fires and the UI shows the
 * "signed out" dialog instead of scattering random per-page error messages.
 */
export function installAuthInterceptor() {
    if (interceptorInstalled) return;
    interceptorInstalled = true;

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const { config, response } = error;
            if (!config || response?.status !== 401) return Promise.reject(error);
            if (config._authRetry) return Promise.reject(error);
            if (SKIP_PATHS.some((p) => (config.url || '').includes(p))) return Promise.reject(error);
            // Nothing to refresh with (public page / already logged out)
            if (!localStorage.getItem('crm_refresh_token')) return Promise.reject(error);

            let newToken = null;
            try {
                newToken = await refreshAccessToken();
            } catch {
                // Transient refresh failure — surface the original error
                return Promise.reject(error);
            }
            if (!newToken) {
                error.sessionExpired = true;
                return Promise.reject(error);
            }

            config._authRetry = true;
            if (typeof config.headers?.set === 'function') {
                config.headers.set('Authorization', `Bearer ${newToken}`);
            } else {
                config.headers = { ...(config.headers || {}), Authorization: `Bearer ${newToken}` };
            }
            return axios(config);
        }
    );
}
